import { useState } from "react";
import { X, Plus, Check, Wand2 } from "lucide-react";
import { AiActionButton } from "@/components/app/AiActionButton";
import { AI } from "@/constants/testIds";

const baseInput =
  "h-11 w-full rounded-field border border-brand-line bg-brand-surface px-3.5 text-[15px] text-brand-text placeholder:text-brand-muted outline-none transition-colors focus:border-brand-accent/60";

export function PromptField({ field, value, error, onChange }) {
  const tid = `prompt-input-${field.id}`;

  const render = () => {
    switch (field.component) {
      case "select":
        return (
          <select
            data-testid={tid}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className={`${baseInput} appearance-none`}
          >
            <option value="" disabled>
              {field.placeholder || "Wybierz…"}
            </option>
            {(field.options || []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        );

      case "textarea":
        return (
          <textarea
            data-testid={tid}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            rows={4}
            className={`${baseInput} h-auto min-h-[110px] resize-y py-2.5 leading-relaxed`}
          />
        );

      case "number":
        return (
          <input
            data-testid={tid}
            type="number"
            value={value ?? ""}
            step={field.validation?.step || "any"}
            min={field.validation?.min}
            max={field.validation?.max}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            className={baseInput}
          />
        );

      case "url":
        return (
          <input
            data-testid={tid}
            type="url"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            className={baseInput}
          />
        );

      case "date":
        return (
          <input
            data-testid={tid}
            type="date"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className={`${baseInput} [color-scheme:dark]`}
          />
        );

      case "color":
        return (
          <div className="flex items-center gap-3">
            <input
              type="color"
              aria-label={`${field.label} — próbnik`}
              value={/^#[0-9A-Fa-f]{6}$/.test(value || "") ? value : "#F2F0EA"}
              onChange={(e) => onChange(e.target.value.toUpperCase())}
              className="h-11 w-14 shrink-0 cursor-pointer rounded-field border border-brand-line bg-brand-surface p-1"
            />
            <input
              data-testid={tid}
              type="text"
              value={value || ""}
              onChange={(e) => onChange(e.target.value.toUpperCase())}
              placeholder={field.placeholder || "#RRGGBB"}
              className={baseInput}
            />
          </div>
        );

      case "multiselect":
        return <ChipMultiSelect field={field} value={value} onChange={onChange} tid={tid} />;

      case "checkbox":
        return (
          <button
            type="button"
            role="checkbox"
            aria-checked={value === true}
            data-testid={tid}
            onClick={() => onChange(value !== true)}
            className={`flex w-full items-start gap-3 rounded-field border px-3.5 py-3 text-left transition-colors ${
              value === true ? "border-brand-accent bg-brand-accent/10" : "border-brand-line bg-brand-surface hover:border-brand-accent/40"
            }`}
          >
            <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
              value === true ? "border-brand-accent bg-brand-accent text-brand-on" : "border-brand-line"
            }`}>
              {value === true && <Check size={14} strokeWidth={3} />}
            </span>
            <span className="text-[14px] text-brand-text">{field.trueLabel || field.label}</span>
          </button>
        );

      case "tags":
        return <TagsInput field={field} value={value} onChange={onChange} tid={tid} />;

      case "text":
      default:
        return (
          <input
            data-testid={tid}
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            className={baseInput}
          />
        );
    }
  };

  const canHint = field.component === "text" || field.component === "textarea";
  return (
    <div data-testid={`prompt-field-${field.id}`} className="space-y-1.5">
      {field.component !== "checkbox" && (
        <div className="flex items-center justify-between gap-2">
          <label className="flex items-center gap-1 text-sm font-medium text-brand-text">
            {field.label}
            {field.required && <span className="text-brand-accent">*</span>}
          </label>
          {canHint && (
            <AiActionButton
              actionKey="field_hint"
              testId={`${AI.fieldHint}-${field.id}`}
              label="Podpowiedź AI"
              icon={Wand2}
              buildPayload={() => ({ fieldLabel: field.label, context: field.help || field.placeholder || "" })}
              onResult={(out) => onChange(out.trim())}
              className="inline-flex items-center gap-1 rounded-full border border-brand-line px-2.5 py-1 text-[11px] font-medium text-brand-sec transition-colors hover:border-brand-accent/60 hover:text-brand-text"
            />
          )}
        </div>
      )}
      {render()}
      {field.help && !error && <p className="text-xs text-brand-muted">{field.help}</p>}
      {error && (
        <p data-testid={`prompt-error-${field.id}`} className="text-xs font-medium text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}

function ChipMultiSelect({ field, value, onChange, tid }) {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (v) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <div data-testid={tid} className="flex flex-wrap gap-2">
      {(field.options || []).map((o) => {
        const active = selected.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => toggle(o.value)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors ${
              active
                ? "border-brand-accent bg-brand-accent/15 text-brand-text"
                : "border-brand-line bg-brand-surface text-brand-sec hover:text-brand-text"
            }`}
          >
            {active && <Check size={13} strokeWidth={2.5} className="text-brand-accent" />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function TagsInput({ field, value, onChange, tid }) {
  const [draft, setDraft] = useState("");
  const tags = Array.isArray(value) ? value : [];
  const add = () => {
    const t = draft.trim();
    if (t && !tags.includes(t)) onChange([...tags, t]);
    setDraft("");
  };
  const remove = (t) => onChange(tags.filter((x) => x !== t));
  return (
    <div>
      <div className="flex gap-2">
        <input
          data-testid={tid}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder={field.placeholder}
          className={baseInput}
        />
        <button
          type="button"
          onClick={add}
          aria-label="Dodaj"
          data-testid={`prompt-tag-add-${field.id}`}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-field bg-brand-accent text-brand-on transition-transform hover:-translate-y-0.5"
        >
          <Plus size={18} strokeWidth={2} />
        </button>
      </div>
      {tags.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-2">
          {tags.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1.5 rounded-full border border-brand-line bg-brand-elevated px-3 py-1.5 text-[13px] text-brand-text"
            >
              {t}
              <button type="button" onClick={() => remove(t)} aria-label={`Usuń ${t}`} className="text-brand-muted hover:text-red-400">
                <X size={13} strokeWidth={2} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
